import React, { useEffect, useState } from 'react'
import { Badge, Button, Card, CardContent, Checkbox, IconTooltip, Input, SinglePick } from '@duck/ui'
import { Link, Mail } from 'lucide-react'
import { apis, useTar } from '@/modules'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { useTasks } from '@/hooks/useTasks'
import { toast } from 'sonner'
import { usePersons, personsQueryKeys } from '@/hooks/usePersons'
import { useCohort } from '@/hooks/useCohorts'
import { useQueryClient } from '@tanstack/react-query'
import { StandardLoader } from '@/components/StandardLoader'
import { AssignmentDateTimePicker } from './AssignmentDateTimePicker'
import { useOrganizationSettings } from '@/hooks/useOrganizationSettings'

const MAX_PERSON_NAMES_TO_SHOW = 3

const combineDateAndTime = (date: Date | undefined, time: string | undefined): Date | undefined => {
  if (!date) return undefined

  // If a time isn't provided, treat it as start-of-day. This keeps comparisons
  // deterministic and forces explicit time selection for same-day ordering.
  const [hoursStr, minutesStr] = (time ?? "00:00").split(":")
  const hours = Number(hoursStr)
  const minutes = Number(minutesStr)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return undefined

  const next = new Date(date)
  next.setHours(hours, minutes, 0, 0)
  return next
}

interface SendAssignmentProps {
  cohortId?: string
  personIds?: string[]
  assignmentId?: string
  sendInvitationEmails?: boolean
  publicMode?: boolean
  onClose?: () => void
  onSend?: () => void
  // When true, the parent already holds the cohort (e.g. V2's board) and refreshes
  // it after a send — so this dialog skips its own /cohorts/:id fetch and refetch.
  parentManagesCohort?: boolean
}

const parseDateTime = (isoString: string | undefined | null): { date: Date | undefined; time: string | undefined } => {
  if (!isoString) return { date: undefined, time: undefined }
  const date = new Date(isoString)
  if (isNaN(date.getTime())) return { date: undefined, time: undefined }
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return { date, time: `${hours}:${minutes}` }
}

export const SendAssignment: React.FC<SendAssignmentProps> = ({ cohortId, personIds: initialPersonIds, assignmentId, sendInvitationEmails, publicMode = false, onClose, onSend, parentManagesCohort = false }) => {
  const { tasks } = useTasks()
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const queryClient = useQueryClient()

  const isEditMode = !!assignmentId

  const [assignmentData, setAssignmentData] = useState<{
    cohortId: string
    personIds: string[]
    ownerId: string
  } | null>(null)
  const [personIds, setPersonIds] = useState<string[]>(initialPersonIds || [])
  const { persons, refetch: refetchPersons } = usePersons(personIds)
  const { refetch: refetchCohort } = useCohort(parentManagesCohort ? undefined : cohortId)
  const { getOrgSetting } = useOrganizationSettings()

  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(undefined)
  const [startEnabled, setStartEnabled] = useState(false)
  const [startDate, setStartDate] = useState<Date | undefined>(() => new Date())
  const [startTime, setStartTime] = useState<string | undefined>(() => {
    const now = new Date()
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
  })

  const [dueEnabled, setDueEnabled] = useState(false)
  const [dueDate, setDueDate] = useState<Date | undefined>(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow
  })
  const [dueTime, setDueTime] = useState<string | undefined>(() => {
    const now = new Date()
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
  })

  const [overdueEnabled, setOverdueEnabled] = useState(false)
  const [overdueDate, setOverdueDate] = useState<Date | undefined>(undefined)
  const [overdueTime, setOverdueTime] = useState<string | undefined>('00:00')

  const TIME_UNIT_MULTIPLIERS: { [unit: string]: number } = {
    minutes: 1,
    hours: 60,
  }
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(false)
  const [timeLimitValue, setTimeLimitValue] = useState<string>('5')
  const [timeLimitUnit, setTimeLimitUnit] = useState<'minutes' | 'hours'>('hours')

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingAssignment, setIsLoadingAssignment] = useState(false)

  // Fetch assignment data when in edit mode
  useEffect(() => {
    if (assignmentId && isEditMode) {
      setIsLoadingAssignment(true)
      apis.partnerService.assignmentsAssignmentIdGet(assignmentId)
        .then((response) => {
          const assignment = response.data.data
          if (assignment) {
            // Extract personIds from assignment (assuming it has personIds or we need to get them from the assignment)
            // For now, we'll use the personIds from the assignment if available
            // The assignment response might not include personIds directly, so we might need to handle this differently
            // But for now, let's assume we have them from initialPersonIds or we'll need to fetch them separately
            const finalCohortId = cohortId || assignment.cohortId || ''
            const finalPersonIds = initialPersonIds || assignment.personIds || []

            setAssignmentData({
              cohortId: finalCohortId,
              personIds: finalPersonIds,
              ownerId: assignment.ownerId,
            })
            setPersonIds(finalPersonIds)
            setSelectedTaskId(assignment.taskId)

            // Parse dates
            const start = parseDateTime(assignment.startDate)
            const due = parseDateTime(assignment.dueDate)
            const overdue = parseDateTime(assignment.overdueDate)

            setStartEnabled(!!assignment.startDate)
            setStartDate(start.date)
            setStartTime(start.time)

            setDueEnabled(!!assignment.dueDate)
            setDueDate(due.date)
            setDueTime(due.time)

            setOverdueEnabled(!!assignment.overdueDate)
            setOverdueDate(overdue.date)
            setOverdueTime(overdue.time)

            const assignmentTimeLimit = assignment.timeLimit
            if (assignmentTimeLimit != null && typeof assignmentTimeLimit === 'number' && assignmentTimeLimit > 0) {
              setTimeLimitEnabled(true)
              if (assignmentTimeLimit % 60 === 0) {
                setTimeLimitUnit('hours')
                setTimeLimitValue(String(assignmentTimeLimit / 60))
              } else {
                setTimeLimitUnit('minutes')
                setTimeLimitValue(String(assignmentTimeLimit))
              }
            } else {
              setTimeLimitEnabled(false)
            }
          }
        })
        .catch((error) => {
          console.error('Failed to load assignment:', error)
          toast.error(t('errors.errorLoadingAssignment'))
        })
        .finally(() => {
          setIsLoadingAssignment(false)
        })
    }
  }, [assignmentId, isEditMode, cohortId, initialPersonIds, t])

  const startAt = startEnabled ? combineDateAndTime(startDate, startTime) : undefined
  const dueAt = dueEnabled ? combineDateAndTime(dueDate, dueTime) : undefined
  const overdueAt = overdueEnabled ? combineDateAndTime(overdueDate, overdueTime) : undefined

  const dueAfterStartError =
    startEnabled && dueEnabled && startAt && dueAt && dueAt <= startAt
      ? t('errors.dueDateMustBeAfterStartDate')
      : undefined

  const overdueAfterDueError =
    dueEnabled && overdueEnabled && dueAt && overdueAt && overdueAt <= dueAt
      ? t('errors.overdueDateMustBeAfterDueDate')
      : undefined

  useEffect(() => {
    if (!dueEnabled) {
      setOverdueEnabled(false)
      setOverdueDate(undefined)
      setOverdueTime(undefined)
    }
  }, [dueEnabled])

  const handleStartEnabledChange = (enabled: boolean) => {
    setStartEnabled(enabled)
    if (enabled) {
      const now = new Date()
      setStartDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()))
      setStartTime(`${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`)
    }
  }

  const handleDueEnabledChange = (enabled: boolean) => {
    setDueEnabled(enabled)
    if (enabled) {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      setDueDate(new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate()))
      const now = new Date()
      setDueTime(`${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`)
    }
  }

  const handleSend = async () => {
    if (!selectedTaskId) {
      toast.error(t('common.selectChallenge'))
      return
    }
    if (dueAfterStartError || overdueAfterDueError) {
      toast.error(t('errors.pleaseFixTimeWindows'))
      return
    }

    const finalCohortId = cohortId || assignmentData?.cohortId
    const finalPersonIds = personIds.length > 0 ? personIds : assignmentData?.personIds || []

    if (!finalCohortId) {
      return
    }
    // Skip personIds validation for public mode
    if (!publicMode && finalPersonIds.length === 0) {
      return
    }

    const invitationEmails = getOrgSetting('invitationEmails') ?? false

    const trimmedTimeLimit = timeLimitValue.trim()
    const parsedTimeLimit = trimmedTimeLimit === '' ? undefined : Number(trimmedTimeLimit)
    if (
      timeLimitEnabled &&
      !(parsedTimeLimit != null && Number.isFinite(parsedTimeLimit) && parsedTimeLimit > 0)
    ) {
      toast.error(t('errors.pleaseFixTimeWindows'))
      return
    }
    const timeLimitMinutes =
      timeLimitEnabled &&
        parsedTimeLimit != null &&
        Number.isFinite(parsedTimeLimit) &&
        parsedTimeLimit > 0
        ? parsedTimeLimit * (TIME_UNIT_MULTIPLIERS[timeLimitUnit] ?? 1)
        : undefined

    setIsLoading(true)
    try {
      if (isEditMode && assignmentId) {
        if (!assignmentData) {
          toast.error(t('errors.errorLoadingAssignment'))
          setIsLoading(false)
          return
        }
        // Update existing assignment
        await apis.partnerService.assignmentsAssignmentIdPut(assignmentId, {
          cohortId: finalCohortId,
          taskId: selectedTaskId,
          ownerId: assignmentData.ownerId,
          timeLimit: timeLimitMinutes,
          startDate: startAt?.toISOString(),
          dueDate: dueAt?.toISOString(),
          overdueDate: overdueAt?.toISOString(),
          personIds: finalPersonIds,
        })
        toast.success(t('common.assignmentUpdated'))
      } else {
        // Create new assignment
        await apis.partnerService.assignmentsPost({
          cohortId: finalCohortId,
          taskId: selectedTaskId,
          timeLimit: timeLimitMinutes,
          startDate: startAt?.toISOString(),
          dueDate: dueAt?.toISOString(),
          overdueDate: overdueAt?.toISOString(),
          personIds: publicMode ? [] : finalPersonIds,
          public: publicMode,
        })
        toast.success(publicMode ? t('partner.publicAssignmentCreated') : (invitationEmails ? t('partner.assignmentSent') : t('partner.assignmentCreated')))
      }
      if (!parentManagesCohort) {
        await refetchCohort()
      }
      await refetchPersons()
      // Invalidate all persons queries to ensure CohortPage refetches persons data
      await queryClient.invalidateQueries({ queryKey: personsQueryKeys.lists() })
      onSend?.()
    } catch (error) {
      console.error(error)
      toast.error(isEditMode ? t('errors.errorUpdatingAssignment') : t('errors.errorSendingAssignment'))
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingAssignment) {
    return (
      <div className="flex items-center justify-center py-8">
        <StandardLoader size="md" />
      </div>
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void handleSend()
  }

  const isTimeLimitSingular = timeLimitValue.trim() === '1'

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        {publicMode && (
          <Card className="bg-primary/5 border border-primary/20">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <Link className="h-5 w-5 text-primary" />
                </div>
                <p className="text-sm text-primary">
                  {t('partner.publicAssignmentDescription')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        {!publicMode && personIds.length > 0 && (
          <div className="flex flex-col gap-4">
            <div className="text-sm font-medium text-muted-foreground">{personIds.length > 1 ? tP('XpeopleCOLON', { count: personIds.length }) : tP('personCOLON')}</div>
            <div className="flex flex-row gap-1">
              {(() => {
                const byId = new Map(persons.map((p) => [p.id, p]))
                return personIds.slice(0, MAX_PERSON_NAMES_TO_SHOW).map((id) => {
                  const p = byId.get(id)
                  const fullName = `${p?.firstName ?? ""} ${p?.lastName ?? ""}`.trim()
                  return (
                    <Badge key={id} className="text-sm font-medium" variant="outline">
                      {fullName || 'Unknown'}
                    </Badge>
                  )
                })
              })()}
              {personIds.length > MAX_PERSON_NAMES_TO_SHOW && (
                <>
                  <Badge className="text-sm font-medium" variant="outline">+{personIds.length - MAX_PERSON_NAMES_TO_SHOW}</Badge>
                </>
              )}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <div className="text-sm font-medium text-muted-foreground">{t('challenges.challenge')}:</div>
          <SinglePick
            options={tasks.map(task => ({ label: task.name, value: task.id ?? '' }))}
            selected={selectedTaskId}
            onChange={setSelectedTaskId}
            labels={{ placeholder: t('common.selectChallenge') }}
            pickIfOne
            disabled={isEditMode}
          />
        </div>

        <div className="space-y-4 flex flex-col items-center">
          <div className="flex flex-col gap-4 w-full">
            <AssignmentDateTimePicker
              label={t('partner.startDate')}
              infoTooltip={tP('assignmentStartDateTooltip')}
              enabled={startEnabled}
              onEnabledChange={handleStartEnabledChange}
              date={startDate}
              onDateChange={setStartDate}
              time={startTime}
              onTimeChange={setStartTime}
            />

            <AssignmentDateTimePicker
              label={t('partner.dueDate')}
              infoTooltip={tP('assignmentDueDateTooltip')}
              enabled={dueEnabled}
              onEnabledChange={handleDueEnabledChange}
              date={dueDate}
              onDateChange={setDueDate}
              time={dueTime}
              onTimeChange={setDueTime}
              error={dueAfterStartError}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1 w-full">
          <div className="flex flex-row gap-1 md:space-x-4 justify-between w-full">
            <label className="flex items-center gap-2 flex-1">
              <Checkbox
                checked={timeLimitEnabled}
                onCheckedChange={(checked) => setTimeLimitEnabled(checked === true)}
              />
              <span className="text-sm font-medium text-muted-foreground">
                {t('partner.timeLimit')}
              </span>
              <IconTooltip
                content={tP('assignmentTimeLimitTooltip')}
                iconClassName="shrink-0"
                tooltipClassName="max-w-[240px]"
              />
            </label>
            <div className="flex gap-2 items-center">
              <Input
                type="number"
                min={1}
                className="w-24"
                value={timeLimitValue}
                onChange={(e) => setTimeLimitValue(e.target.value)}
                disabled={!timeLimitEnabled}
              />
              <SinglePick
                options={[
                  { label: isTimeLimitSingular ? t('common.hour') : t('common.hours'), value: 'hours' },
                  { label: isTimeLimitSingular ? t('common.minute') : t('common.minutes'), value: 'minutes' },
                ]}
                selected={timeLimitUnit}
                onChange={(value) => setTimeLimitUnit((value as 'minutes' | 'hours') ?? 'minutes')}
                className="w-32"
                searchable={false}
                disabled={!timeLimitEnabled}
              />
            </div>
          </div>
        </div>
        <div className="text-xs text-muted-foreground">
          {t('common.timezone')}: {Intl.DateTimeFormat().resolvedOptions().timeZone} (GMT{(() => {
            const offset = -new Date().getTimezoneOffset()
            const sign = offset >= 0 ? '+' : '-'
            const hours = Math.floor(Math.abs(offset) / 60)
            const minutes = Math.abs(offset) % 60
            return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
          })()})
        </div>
        {!publicMode && sendInvitationEmails && !isEditMode && (
          <Card className="bg-accent/10 border border-accent/20">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <Mail className="h-5 w-5 text-accent" />
                </div>
                <p className="text-sm text-accent">
                  {personIds.length === 1
                    ? t('common.thisActionWillSendEmailInvitation')
                    : t('common.thisActionWillSendEmailInvitations', { count: personIds.length })
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        {!publicMode && (!sendInvitationEmails || isEditMode) && (
          <Card className="bg-muted/30 border border-muted/60">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">{t('common.noEmailsWillBeSent')}</p>
              </div>
            </CardContent>
          </Card>
        )}
        <div className="flex flex-row justify-between gap-4 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            className="w-36 flex items-center justify-center gap-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <StandardLoader size="sm" inline />
                <span>{isEditMode ? (t('common.updating')) : (publicMode ? t('common.creating') : t('common.sending'))}</span>
              </>
            ) : (
              isEditMode ? (t('common.update') || 'Update') : (publicMode ? t('common.create') : t('common.send'))
            )}
          </Button>
        </div>
      </div>
    </form>
  )
}
